import { Link } from 'react-router';
import {
  Badge,
  Button,
  Card,
  CapacityMeter,
  ErrorState,
  FlowBar,
  Skeleton,
  SkeletonText,
  NO_DATA_DASH,
  Sparkline,
  formatNumber,
} from '@mgdlead/ui';
import { Link2 } from 'lucide-react';

import { CAMPAIGN_STATUS } from '../campaign/campaign-ui';
import type { CampaignCardData, CampaignCardsData } from '../../lib/api';
import { humanizeError } from '../../lib/errors';

/**
 * G5 (07/10): a lista de campanhas em CARDS, com o dia de hoje de cada uma.
 *
 * A árvore de custo (CPL Meta × CPL real, aberta por campanha da Meta) continua existindo como
 * a outra aba: é a leitura de quem decide orçamento. O card é a de quem abre o celular 15 vezes
 * por dia e quer saber "está entrando gente? está lotando?".
 *
 * Tudo aqui chega pronto de `analytics.campaignCards` (invariante 3): o minigráfico é a série
 * por hora, a barra é o fluxo que fecha, a lotação já vem dividida.
 */
export function CampaignCardsGrid({
  query,
  campaignHref,
  onVincularGrupo,
}: {
  query: {
    data: CampaignCardsData | null | undefined;
    isPending: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => unknown;
  };
  campaignHref: (campaignId: string) => string;
  /** Ausente = o papel não vincula grupo; o card só avisa. */
  onVincularGrupo?: ((campaignId: string) => void) | undefined;
}) {
  if (query.isError) {
    return (
      <Card>
        <ErrorState
          title="Não conseguimos carregar o dia das campanhas"
          description={humanizeError(query.error)}
          onRetry={() => void query.refetch()}
        />
      </Card>
    );
  }
  if (query.isPending || !query.data) {
    return (
      <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <li key={i}>
            <Card padded={false} className="flex flex-col gap-3 p-4">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-7 w-16" />
              <Skeleton className="h-10 w-full" />
              <SkeletonText lines={2} />
            </Card>
          </li>
        ))}
      </ul>
    );
  }
  if (query.data.campanhas.length === 0) return null;

  return (
    <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" aria-label="Campanhas hoje">
      {query.data.campanhas.map((c) => (
        <li key={c.campaignId}>
          <CampaignDayCard
            c={c}
            href={campaignHref(c.campaignId)}
            onVincularGrupo={onVincularGrupo ? () => onVincularGrupo(c.campaignId) : undefined}
          />
        </li>
      ))}
    </ul>
  );
}

function CampaignDayCard({
  c,
  href,
  onVincularGrupo,
}: {
  c: CampaignCardData;
  href: string;
  onVincularGrupo?: (() => void) | undefined;
}) {
  const status = CAMPAIGN_STATUS[c.status];
  const semGrupo = c.grupos === 0;
  return (
    <Card padded={false} className="flex h-full flex-col gap-3 p-4" data-campaign={c.campaignId}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link
            to={href}
            className="block truncate font-medium text-foreground underline-offset-2 hover:underline"
          >
            {c.name}
          </Link>
          <span className="font-mono text-2xs uppercase tracking-caps text-subtle-foreground">
            {c.code}
          </span>
        </div>
        <Badge tone={status.tone} dot title={status.hint}>
          {status.label}
        </Badge>
      </div>

      {semGrupo ? (
        <div className="flex flex-col gap-2 rounded-md border border-dashed border-border-strong p-3">
          <Badge tone="warning" dot className="self-start">
            Sem grupo vinculado
          </Badge>
          <p className="text-xs text-muted-foreground">
            Sem grupo, a campanha não manda ninguém para lugar nenhum.
          </p>
          {onVincularGrupo ? (
            <Button
              size="sm"
              leadingIcon={<Link2 />}
              onClick={onVincularGrupo}
              className="self-start max-md:w-full"
            >
              Vincular grupo
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Entradas hoje</p>
              <p className="nums text-2xl font-semibold text-foreground">
                {c.entradasHoje === null ? NO_DATA_DASH : formatNumber(c.entradasHoje)}
              </p>
              {c.semMedicao ? (
                <p className="text-2xs text-muted-foreground">
                  {semMedicaoTexto(c.semMedicao.grupos, c.grupos)}
                </p>
              ) : null}
            </div>
            <Sparkline
              className="max-w-40"
              slots={24}
              values={c.porHora}
              label={`Entradas de hoje em ${c.name}, hora a hora`}
            />
          </div>
          <FlowBar
            compact
            entraram={c.entradasHoje === null ? null : c.fluxo.entraram}
            ficaram={c.fluxo.ficaram}
            sairam={c.fluxo.sairam}
            removidos={c.fluxo.removidos}
            semCausa={c.fluxo.semCausa}
            periodLabel="hoje"
          />
          <CapacityMeter
            fraction={c.lotacao?.fracao ?? null}
            vacancies={c.lotacao?.vagas ?? null}
          />
        </>
      )}
    </Card>
  );
}

/** O motivo do "—" (ou do número parcial): nenhum número conectado enxerga o grupo agora. */
export function semMedicaoTexto(semMedicao: number, grupos: number): string {
  if (semMedicao >= grupos) return 'Sem medição: nenhum número conectado vê estes grupos agora.';
  return `${semMedicao} de ${grupos} grupos sem medição agora (número desconectado).`;
}
